/**
 * Los planes de Aturno. Fuente única: la usan la landing y el selector con el
 * que se paga (components/pricing-section.jsx, en /admin/plans y en el paso de
 * planes del onboarding).
 *
 * Antes eran dos listas distintas y no coincidían: la landing ofrecía "Business"
 * y "Business Pro", y el selector transaccional mostraba "Negocio", "Negocio
 * Pro" y "Negocio Pro+", con los dos últimos marcados como "Próximamente"
 * aunque el backend sí los cobra. O sea que se anunciaba un plan con un nombre
 * y se cobraba con otro, y uno que la landing vendía no se podía contratar.
 *
 * Los precios salen de TIERS en backend/server.js, que es lo que realmente se
 * cobra. Si cambiás uno, cambialo en los dos lados.
 *
 * Sobre el plan gratuito: todavía no existe en el backend. Hoy lo que hay es
 * una prueba de 14 días, así que eso es lo que se anuncia. Cuando el plan
 * gratuito con comisión esté implementado, la primera columna se reemplaza por
 * él y el diseño no cambia.
 */

export const planes = [
  {
    id: 'trial',
    nombre: 'Prueba gratis',
    bajada: 'Para arrancar hoy',
    precio: 0,
    precioTexto: '$0',
    periodo: '14 días',
    etiqueta: 'Sin tarjeta',
    destacado: false,
    cta: 'Empezar gratis',
    features: [
      'Todas las funcionalidades',
      'Reservas ilimitadas',
      'Bot de WhatsApp',
      'Sin tarjeta de crédito',
    ],
  },
  {
    id: 'business-starter',
    nombre: 'Business',
    // Lo que necesita el cobro: Mercado Pago trabaja en centavos y el alta de
    // suscripción decide con `diasDePrueba` si arranca con prueba o cobrando.
    precioEnCentavos: 1499900,
    diasDePrueba: 14,
    bajada: 'Para tu negocio',
    precio: 14999,
    precioTexto: '$14.999',
    periodo: '/mes',
    etiqueta: 'Más elegido',
    destacado: true,
    cta: 'Elegir Business',
    features: [
      'Reservas ilimitadas',
      'Hasta 25 personas en el equipo',
      'Hasta 50 recursos o espacios',
      'Cobro de señas con Mercado Pago',
      'Google Calendar y recordatorios',
      'Marca propia en tu página',
    ],
  },
  {
    id: 'business-pro',
    nombre: 'Business Pro',
    precioEnCentavos: 3499900,
    // Sin prueba: la prueba es para arrancar, y a este se llega desde el de
    // abajo o sabiendo lo que se busca.
    diasDePrueba: 0,
    bajada: 'Para escalar sin techo',
    precio: 34999,
    precioTexto: '$34.999',
    periodo: '/mes',
    etiqueta: 'Sin límites',
    destacado: false,
    cta: 'Elegir Business Pro',
    features: [
      'Todo lo de Business, más:',
      'Servicios y recursos ilimitados',
      'Hasta 100 personas en el equipo',
      'Múltiples sucursales',
      'Reportes y analíticas completas',
      'Soporte prioritario',
    ],
  },
  {
    id: 'business-pro-plus',
    nombre: 'Business Pro Plus',
    precioEnCentavos: 4999900,
    diasDePrueba: 0,
    bajada: 'Para cadenas y franquicias',
    precio: 49999,
    precioTexto: '$49.999',
    periodo: '/mes',
    etiqueta: 'Todo incluido',
    destacado: false,
    cta: 'Elegir Business Pro Plus',
    features: [
      'Todo lo de Business Pro, más:',
      'Equipo sin límite de personas',
      'Bot de WhatsApp con IA',
      'Analíticas avanzadas',
      'Acceso a la API',
      'Soporte dedicado',
    ],
  },
];

const porId = (id) => planes.find((p) => p.id === id);

/**
 * **Los tres planes que Aturno ofrece**: Starter, Business y Business Pro.
 *
 * Es lo que se ve en los tres lados donde se muestran planes —la landing, el
 * paso de planes del onboarding y /admin/plans—, y por eso es una sola lista.
 * Antes de esto la landing y el onboarding mostraban cuatro tarjetas con los
 * nombres viejos (Business / Business Pro / Business Pro Plus, más la de la
 * prueba), o sea que el mismo plan se anunciaba con un nombre y se cobraba con
 * otro, y desde el onboarding se podía contratar un cuarto plan que no se
 * ofrece.
 *
 * Es una vista de `planes`, no una segunda lista: los precios y el `id` con el
 * que se cobra salen de los mismos objetos de arriba y acá sólo se cambia cómo
 * se llaman y en qué orden van. Una copia con los precios escritos de nuevo es
 * exactamente el bug que este archivo vino a cerrar.
 *
 * Ojo con los nombres: `nombre` es lo que ve el cliente, `id` es lo que se le
 * manda al backend y decide el precio. Starter se cobra como `business-starter`
 * ($14.999) y Business como `business-pro` ($34.999).
 *
 * La prueba gratis no está acá porque no es un plan: es el estado en el que
 * arranca una cuenta. Se ofrece en el hero, en el cierre de la landing y con su
 * propio botón dentro de la tarjeta de Starter en el onboarding.
 */
export const planesDeContratacion = [
  {
    ...porId('business-starter'),
    nombre: 'Starter',
    bajada: 'Para arrancar con turnos online',
    cta: 'Elegir Starter',
    // Sin etiqueta ni destaque: el que se destaca es el del medio.
    etiqueta: null,
    destacado: false,
  },
  {
    ...porId('business-pro'),
    nombre: 'Business',
    bajada: 'Para un negocio que ya no entra en una agenda',
    cta: 'Elegir Business',
    etiqueta: 'Más elegido',
    destacado: true,
    // La primera viñeta de business-pro dice "Todo lo de Business, más:", que
    // era cierto con los nombres viejos. Con estos, el de abajo es Starter.
    // Se reescribe sólo ese renglón y el resto sale del plan de arriba.
    features: ['Todo lo de Starter, más:', ...porId('business-pro').features.slice(1)],
  },
  {
    // `enterprise` ya existe en backend/src/planes.js con `cobrable: false` y la
    // nota "se vende hablando, no por la web". `cotizado` es lo que hace que la
    // tarjeta muestre el botón de contacto en vez del de pago: sin precio no hay
    // nada que cobrar, y el backend además rechazaría el alta.
    id: 'enterprise',
    nombre: 'Business Pro',
    bajada: 'Para cadenas, franquicias y operaciones a medida',
    cotizado: true,
    precioTexto: 'A medida',
    periodo: '',
    etiqueta: 'Cotizado',
    destacado: false,
    cta: 'Agendá con nosotros',
    /**
     * A dónde lleva el botón: a la página pública de Aturno, a sacar un turno
     * como lo haría un cliente de cualquier negocio de la plataforma.
     *
     * Antes abría WhatsApp con un mensaje escrito. Mandar a alguien a escribir
     * un mensaje deja la conversación esperando que alguien la lea y conteste;
     * acá elige un horario y queda agendado, que es lo mismo que este plan le
     * va a vender a él. La demo del producto es usarlo.
     *
     * Es una ruta interna: `/:slug` es el comodín de la página pública, así que
     * `/aturno` es el negocio con ese slug y NO hace falta declararla en App.js.
     * Sí hace falta que ese negocio exista y esté presentable, porque es lo
     * primero que ve alguien que está por contratar el plan más caro.
     */
    enlace: '/aturno',
    features: [
      'Todo lo de Business, sin límites:',
      'Equipo, servicios y recursos ilimitados',
      'Múltiples sucursales',
      'Bot de WhatsApp con IA',
      'Acceso a la API',
      'Soporte dedicado',
    ],
  },
];

/**
 * El nombre que ve el cliente, a partir del id que se guarda.
 *
 * El onboarding mostraba `formData.selectedPlan` crudo, o sea "business-starter"
 * en la pantalla anterior a pagar: el id es lo que se le manda al backend y no
 * se renombra nunca —renombrarlo rompe las suscripciones existentes—, así que
 * hace falta traducirlo para mostrarlo. Y justo ahí importa: el nombre con el
 * que alguien compra tiene que ser el mismo que después ve en su panel.
 *
 * Busca primero en planesDeContratacion, que es la lista con los nombres
 * vigentes (Starter / Business / Business Pro); `planes` todavía tiene los
 * viejos para los ids que no se contratan por la web.
 */
export const nombreDePlan = (id) =>
  planesDeContratacion.find((p) => p.id === id)?.nombre ||
  planes.find((p) => p.id === id)?.nombre ||
  id;
